import { useEffect, useState, useCallback } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import { useAlert } from "../../../Hooks/useAlert";
import { dairyInfoAPI } from "../../../services/api";
import settingsLanguage from "../../../language/settingsLanguage";
import Alert from "../../common/Alert";
import BackButton from "./BackButton";
import FileUpload from "./FileUpload";

function DairyInfoPage() {
  const { language } = useLanguage();
  const text = settingsLanguage[language];
  const { showAlert, alertConfig } = useAlert();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [dairyName, setDairyName] = useState("");
  const [dairyLogoFile, setDairyLogoFile] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [currentQr, setCurrentQr] = useState(null);

  const loadDairyInfo = useCallback(async () => {
    setFetching(true);
    try {
      const response = await dairyInfoAPI.getDairyInfo();
      if (response.success && response.data) {
        setDairyName(response.data.dairyName || "");
        setCurrentLogo(response.data.dairyLogo || null);
        setCurrentQr(response.data.paymentQRImage || null);
      }
    } catch (error) {
      console.error("Error loading dairy info:", error);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    loadDairyInfo();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadDairyInfo();
      }
    };

    const handleFocus = () => {
      loadDairyInfo();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadDairyInfo]);

  const handleSaveDairy = async () => {
    if (!dairyName || dairyName.trim() === "") {
      await showAlert({
        type: "warning",
        title: "Validation Error",
        message: "Dairy name is required",
      });
      return;
    }

    const formData = new FormData();
    formData.append("DairyName", dairyName.trim());
    if (dairyLogoFile) formData.append("DairyLogo", dairyLogoFile);
    if (qrFile) formData.append("PaymentQRImage", qrFile);

    setLoading(true);
    try {
      const response = await dairyInfoAPI.saveDairyInfo(formData);
      if (response.success) {
        await showAlert({
          type: "success",
          title: "Success",
          message: "Dairy information saved successfully!",
        });
        loadDairyInfo();
        setDairyLogoFile(null);
        setQrFile(null);
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: response.message || "Save failed",
        });
      }
    } catch (err) {
      console.error(err);
      await showAlert({
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Upload failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" section="master" />
        <h1 className="settings-page-title">{text.dairyInformation}</h1>
      </header>

      <main className="settings-page-content">
        <div className="settings-form-card">
          <div className="settings-form-body">
            <div className="settings-form-group">
              <label className="settings-form-label">{text.dairyName}</label>
              <input
                type="text"
                value={dairyName}
                onChange={(e) => setDairyName(e.target.value)}
                className="settings-form-input"
                placeholder={text.enterDairyName}
              />
            </div>

            <div className="settings-form-row">
              <FileUpload
                label={text.dairyLogo}
                icon="fa-store"
                currentImage={currentLogo}
                file={dairyLogoFile}
                onFileChange={setDairyLogoFile}
                onRemove={() => setDairyLogoFile(null)}
              />
              <FileUpload
                label={text.paymentQrImage}
                icon="fa-qrcode"
                currentImage={currentQr}
                file={qrFile}
                onFileChange={setQrFile}
                onRemove={() => setQrFile(null)}
              />
            </div>

            <div className="settings-form-actions">
              <button
                type="button"
                onClick={handleSaveDairy}
                disabled={loading || fetching}
                className="settings-form-btn"
              >
                <i className="fas fa-save" />
                <span>{loading ? text.saving : fetching ? text.saving : text.saveDairyInfo}</span>
              </button>
            </div>
          </div>
        </div>
      </main>

      {alertConfig && <Alert {...alertConfig} />}
    </div>
  );
}

export default DairyInfoPage;
